// Package definitions with quotas
export const PR_PACKAGES = {
  PREMIUM: 'Premium PR',
  ADVANCED: 'Advanced PR',
  STANDARD: 'Standard PR',
  LITE: 'PR Lite',
};

// Quotas for each package: { successful, submitted }
export const PACKAGE_QUOTAS = {
  [PR_PACKAGES.PREMIUM]: { successful: 5, submitted: 5 },
  [PR_PACKAGES.ADVANCED]: { successful: 2, submitted: 5 },
  [PR_PACKAGES.STANDARD]: { successful: 1, submitted: 5 },
  [PR_PACKAGES.LITE]: { successful: 1, submitted: 5 },
};

/**
 * Normalize a company name to kebab-case for lookup
 * @param {string} companyName - The display company name
 * @returns {string} The normalized key
 */
export const normalizeCompanyName = (companyName) => {
  return companyName
    .toLowerCase()
    .replace(/[&+]/g, '') // Remove & and + symbols
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Get the company data object
 * @param {string} companyName - The company name (display name)
 * @param {object} [data] - Company data object (from useCompanyData hook)
 * @returns {object|null} The company data { leadId, package } or null
 */
export const getCompanyData = (companyName, data = {}) => {
  const normalizedName = normalizeCompanyName(companyName);
  return data[normalizedName] || null;
};

/**
 * Get the primary lead user ID for a company
 * @param {string} companyName - The company name (display name)
 * @param {object} [companyDataSource] - Company data object (from useCompanyData hook)
 * @returns {number|null} The user ID or null if not assigned
 */
export const getPrimaryLeadId = (companyName, companyDataSource = {}) => {
  const data = getCompanyData(companyName, companyDataSource);
  return data?.leadId || null;
};

/**
 * Get the primary lead name for a company using the users array
 * @param {string} companyName - The company name (display name)
 * @param {Array} users - Array of user objects from useUsers hook
 * @param {object} [companyDataSource] - Company data object (from useCompanyData hook)
 * @returns {string} The lead first name or '----' if not assigned
 */
export const getPrimaryLeadName = (companyName, users = [], companyDataSource = {}) => {
  const userId = getPrimaryLeadId(companyName, companyDataSource);
  if (!userId) {
    return '----';
  }
  const user = users.find((u) => u.id === userId);
  if (!user) {
    return '----';
  }
  return user.firstName;
};

/**
 * Get the PR package for a company
 * @param {string} companyName - The company name (display name)
 * @param {object} [companyDataSource] - Company data object (from useCompanyData hook)
 * @returns {string|null} The package type or null if not assigned
 */
export const getCompanyPackage = (companyName, companyDataSource = {}) => {
  const data = getCompanyData(companyName, companyDataSource);
  return data?.package || null;
};

/**
 * Get the quotas for a company based on their package
 * Companies can have custom quota overrides in their data object.
 * Custom quotas are merged with package defaults (custom values take precedence).
 * @param {string} companyName - The company name (display name)
 * @param {object} [companyDataSource] - Company data object (from useCompanyData hook)
 * @returns {object|null} The quotas { successful, submitted } or null if no package
 * @example
 * // Company with no custom quotas - uses package defaults
 * getCompanyQuotas('bergeron-clifford', companyData) // Returns { successful: 5, submitted: 5 } (Premium)
 * @example
 * // Company with custom quotas override
 * getCompanyQuotas('some-company', companyData) // Returns custom { successful: 10, submitted: 10 }
 * @example
 * // Company with partial override - merges with package defaults
 * getCompanyQuotas('partial-company', companyData) // Returns { successful: 3, submitted: 5 } (custom successful, package submitted)
 */
export const getCompanyQuotas = (companyName, companyDataSource = {}) => {
  const data = getCompanyData(companyName, companyDataSource);
  if (!data?.package) {
    return null;
  }

  const packageQuotas = PACKAGE_QUOTAS[data.package];

  // If company has custom quotas, merge with package defaults
  if (data.quotas) {
    return {
      ...packageQuotas,
      ...data.quotas,
    };
  }

  return packageQuotas;
};
